import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import apiHelpers from '../../utils/apiHelpers';

const WishlistContext = createContext({
  cardIds: {},
  loaded: false,
  isWishlisted: () => false,
  addCard: () => {},
  removeCard: () => {},
  reload: () => {},
});

/**
 * Membership of the user's wishlist as a { cardId: true } map, loaded once, so
 * the wishlist button and the Deck Builder's "En wishlist" tag can answer "is
 * this card wishlisted?" anywhere a card renders. GET /wishlist is far too heavy
 * for that — it hydrates every card's prices and kicks off a price refresh.
 *
 * Mirrors CollectionContext, including its optimistic apply / rollback.
 */
export const WishlistProvider = ({ children }) => {
  const [cardIds, setCardIds] = useState({});
  // Until this is true the buttons keep their neutral defaults, so they don't
  // flash the wrong label on first paint.
  const [loaded, setLoaded] = useState(false);

  const reload = useCallback(() => {
    return apiHelpers
      .getWishlistCardIds()
      .then(response => {
        const map = {};
        (response.data.cardIds || []).forEach(id => {
          map[id] = true;
        });
        setCardIds(map);
        setLoaded(true);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const apply = useCallback((cardId, wishlisted, request) => {
    let previous;
    setCardIds(prev => {
      previous = Boolean(prev[cardId]);
      const next = { ...prev };
      if (wishlisted) next[cardId] = true;
      else delete next[cardId];
      return next;
    });

    return request().catch(() => {
      setCardIds(prev => {
        const next = { ...prev };
        if (previous) next[cardId] = true;
        else delete next[cardId];
        return next;
      });
    });
  }, []);

  const addCard = useCallback(
    cardId => apply(cardId, true, () => apiHelpers.addCardToWishlist(cardId)),
    [apply],
  );

  const removeCard = useCallback(
    cardId =>
      apply(cardId, false, () => apiHelpers.removeCardFromWishlist(cardId)),
    [apply],
  );

  const isWishlisted = useCallback(
    cardId => Boolean(cardIds[cardId]),
    [cardIds],
  );

  return (
    <WishlistContext.Provider
      value={{ cardIds, loaded, isWishlisted, addCard, removeCard, reload }}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => useContext(WishlistContext);

export default WishlistContext;
